import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Field, OptionSelect } from "@/components/FormBits";
import { FacilityStatusBadge } from "@/components/StatusBadges";
import { api, formatApiError } from "@/lib/api";
import { FACILITY_STATUS, eur, fmtDateTime } from "@/lib/constants";

const STATUS_LABELS = Object.fromEntries(Object.entries(FACILITY_STATUS).map(([k, v]) => [k, v.label]));
const Card = ({ title, children, testId }) => (
  <section data-testid={testId} className="rounded-xl border border-slate-200 bg-white p-6">
    <h2 className="font-serif text-2xl font-semibold text-slate-900 mb-4">{title}</h2>
    {children}
  </section>
);

export const StatusPanel = ({ facility, onChange }) => {
  const [status, setStatus] = useState(facility.status);
  const [note, setNote] = useState("");
  const save = async () => {
    try {
      const { data } = await api.patch(`/facilities/${facility.slug}/status`, { status, note });
      toast.success("Stato aggiornato");
      setNote("");
      onChange(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card title="Stato della struttura" testId="status-panel">
      <div className="flex items-center gap-3"><FacilityStatusBadge status={facility.status} testId="current-facility-status" /></div>
      {facility.status_note && <p className="mt-2 text-sm text-slate-700" data-testid="current-status-note">{facility.status_note}</p>}
      <p className="mt-1 font-mono text-[11px] text-slate-400">Aggiornato {fmtDateTime(facility.status_updated_at)} {facility.status_updated_by && `· ${facility.status_updated_by}`}</p>
      <div className="mt-5 space-y-3 border-t border-slate-100 pt-5">
        <Field label="Nuovo stato"><OptionSelect testId="facility-status-select" value={status} onChange={setStatus} options={STATUS_LABELS} /></Field>
        <Field label="Motivazione / nota"><Textarea data-testid="facility-status-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. Intervento impianto di riscaldamento fino al 15/07" /></Field>
        <Button onClick={save} data-testid="facility-status-save-button" className="w-full bg-[#1E293B] hover:bg-slate-800">Aggiorna stato</Button>
      </div>
    </Card>
  );
};

export const TariffPanel = ({ facility, onChange }) => {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm({ hourly_rate: facility.hourly_rate, daily_rate: facility.daily_rate, capacity: facility.capacity, tariff_notes: facility.tariff_notes });
  }, [facility, edit]);
  const save = async () => {
    try {
      const payload = { ...form, hourly_rate: Number(form.hourly_rate), daily_rate: Number(form.daily_rate), capacity: Number(form.capacity) };
      const { data } = await api.patch(`/facilities/${facility.slug}`, payload);
      toast.success("Tariffe aggiornate");
      setEdit(false);
      onChange(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  if (!edit) return (
    <Card title="Tariffe" testId="tariff-panel">
      <dl className="grid grid-cols-2 gap-4">
        <div><dt className="eyebrow">Oraria</dt><dd className="font-mono text-xl" data-testid="tariff-hourly">{eur(facility.hourly_rate)}</dd></div>
        <div><dt className="eyebrow">Giornaliera</dt><dd className="font-mono text-xl" data-testid="tariff-daily">{eur(facility.daily_rate)}</dd></div>
        <div><dt className="eyebrow">Capienza</dt><dd className="font-mono text-xl">{facility.capacity}</dd></div>
      </dl>
      {facility.tariff_notes && <p className="mt-4 text-sm text-slate-600 whitespace-pre-line">{facility.tariff_notes}</p>}
      <Button variant="outline" onClick={() => setEdit(true)} data-testid="tariff-edit-button" className="mt-5 w-full">Modifica tariffe</Button>
    </Card>
  );
  return (
    <Card title="Tariffe" testId="tariff-panel">
      <div className="grid grid-cols-2 gap-3">
        <Field label="€ / ora"><Input data-testid="tariff-hourly-input" type="number" step="0.01" value={form.hourly_rate} onChange={set("hourly_rate")} /></Field>
        <Field label="€ / giorno"><Input data-testid="tariff-daily-input" type="number" step="0.01" value={form.daily_rate} onChange={set("daily_rate")} /></Field>
        <Field label="Capienza" className="col-span-2"><Input data-testid="tariff-capacity-input" type="number" value={form.capacity} onChange={set("capacity")} /></Field>
        <Field label="Note tariffarie (riduzioni, cauzioni…)" className="col-span-2"><Textarea data-testid="tariff-notes-input" rows={3} value={form.tariff_notes} onChange={set("tariff_notes")} /></Field>
      </div>
      <div className="mt-4 flex gap-2">
        <Button variant="outline" onClick={() => setEdit(false)} className="flex-1" data-testid="tariff-cancel-button">Annulla</Button>
        <Button onClick={save} className="flex-1 bg-[#8B1E2B] hover:bg-[#701621]" data-testid="tariff-save-button">Salva</Button>
      </div>
    </Card>
  );
};
